import { useEffect, useRef, useState } from 'react';
import { businessApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { Toggle } from '@/components/inspector/toggle';

// =====================================================================
// ReputationStep (WP3 Task 10) — the wizard's Reputation step BODY (spec
// §8 step 3, ONE screen). WizardStep chrome (title, continue/skip, "step N
// of 4") is supplied by the shell (Task 11); this component only renders
// what goes inside it.
//
// AMENDED by CONSOLIDATION §A2, then T1.8: notification prefs are PER-MEMBER
// (`PATCH /api/b/:businessId/member-prefs`, T5's router), not per-site — the original
// brief's `/api/sites/:id/notifications` endpoint is VOID. `siteId` is only
// used for the "see your reviews" link.
//
// Save contract: `registerSave` fires exactly once (mirrors
// visibility-step.tsx's pattern) and reads the checkbox's CURRENT value via
// a ref at call time, so a toggle after mount is still captured without
// re-registering on every keystroke. Per the controller clarification, the
// save always PATCHes the current checkbox value on Continue — it does not
// try to detect "did the owner change anything" (skip never calls save at
// all, which is the honest no-write path per D6: absent row already reads
// as ON).
//
// The approval-rule copy is a REQUIRED verbatim deliverable (locked #16/#18)
// — checked against packages/shared/src/glossary.ts first; no canonical
// string exists there for this rule, so this is the copy of record.
//
// Fix round 1 (consent issue): the checkbox now initializes from the SAVED
// pref (`GET /api/b/:businessId/member-prefs`) instead of always starting at the D6
// default, so a returning owner who previously opted out never gets their
// choice silently flipped back to true by an unrelated Continue click. The
// simplicity of "just PATCH the current value" stays on the WRITE side only
// — reads must reflect truth. Absent row reads back as `true` from the
// accessor already (D6), so the checked-default still emerges naturally for
// a first-time owner; a fetch failure ALSO falls back to the checked
// default and lets the owner proceed (the save PATCH is still an explicit
// action, never silent). While the GET is in flight the checkbox renders
// disabled+checked — an honest "we don't know yet" moment with no layout
// jump, matching the eventual resolved state either way.
//
// Readiness gate (WP3 Task 11 review, carried item 2): `registerSave` used
// to fire unconditionally on mount, before this GET could possibly have
// resolved — a fast-clicking owner hitting the shell's Continue in that
// window would PATCH whatever `reviewAlertsRef` still held (the D6 checked
// default), silently overwriting a previously-saved opt-out. Registration
// now happens inside the GET's own `.finally()`, so it fires exactly once,
// only after this step's state is truthful. A Continue that beats the fetch
// finds nothing registered — the shell's own default no-op resolve runs
// instead, writing nothing (defaults hold, D6-correct, no silent flip).
// Pinned by reputation-pulse.test.tsx's "readiness gate" test.
// =====================================================================

const APPROVAL_RULE_COPY =
  "nothing posts without your OK — pageboss drafts review replies, you approve every single one before it goes to Google.";

interface EmailPrefsGetResponse {
  prefs: {
    weeklyDigest: boolean;
    reviewNotifications: boolean;
  };
}

export function ReputationStep({
  siteId,
  registerSave,
}: {
  siteId: string;
  registerSave?: (save: () => Promise<void>) => void;
}) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const [reviewAlerts, setReviewAlerts] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsFetchFailed, setPrefsFetchFailed] = useState(false);
  const reviewAlertsRef = useRef(reviewAlerts);

  useEffect(() => {
    reviewAlertsRef.current = reviewAlerts;
  }, [reviewAlerts]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<EmailPrefsGetResponse>(businessApi(businessId, 'member-prefs'))
      .then((res) => {
        if (!cancelled) setReviewAlerts(res.prefs.reviewNotifications);
      })
      .catch(() => {
        // Fetch failure: leave reviewAlerts at the D6 checked default and
        // let the owner proceed — the save PATCH on Continue is still an
        // explicit user action, never a silent write. Flag the failure so we
        // can render an honest note.
        setPrefsFetchFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setPrefsLoading(false);
        // Readiness gate (WP3 Task 11 review, carried item 2): register the
        // Continue-save only once this step's own saved-prefs fetch has
        // settled, not on mount. A fast-clicking owner who hits Continue
        // before this GET resolves finds nothing registered yet — the
        // shell's Continue then awaits its own default no-op resolve and
        // writes nothing (defaults hold, D6-correct) instead of racing
        // reviewAlertsRef's still-default value and silently flipping a
        // previously-saved opt-out back on.
        registerSave?.(async () => {
          await api.patch(businessApi(businessId, 'member-prefs'), { reviewNotifications: reviewAlertsRef.current });
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border-subtle bg-card p-6">
        {/* T12/R2: a notification preference is a switch, not a checkbox. */}
        <Toggle
          label="Email me when a new review lands"
          checked={reviewAlerts}
          disabled={prefsLoading}
          onChange={setReviewAlerts}
        />
        {prefsFetchFailed && !prefsLoading && (
          <p className="mt-2 text-sm text-text-secondary">
            Couldn't load your saved preference — showing the default
          </p>
        )}
        <p className="mt-2 text-sm text-text-secondary">
          Review alerts arrive by email once your Google profile is connected
        </p>
      </div>

      <div className="rounded-xl border border-border-subtle bg-surface-sunken p-6">
        <p className="text-sm font-semibold leading-relaxed text-foreground">
          {APPROVAL_RULE_COPY}
        </p>
      </div>

      <Link
        to={locationRoutes.reviews(businessId, siteId)}
        className="inline-block text-sm font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
      >
        See your reviews →
      </Link>
    </div>
  );
}
